import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAccountData } from "../../../hooks/useAccountData";
import { GCChannel } from "../../../types";
import type { ActiveSearchContext, ChannelSearchContext, SearchContext } from "../types";
import { isChannelContext } from "../types";

export type ChannelMeta = {
  icon: string | undefined;
  displayName: string;
  placeholder?: string;
};

type SearchContextState = {
  context: ActiveSearchContext;
  channelMeta: ChannelMeta | undefined;
  /** Whether the current page is a channel page (regardless of active context) */
  isOnChannelPage: boolean;
  setContext: (ctx: SearchContext) => void;
  restoreChannelContext: () => void;
  commitSearch: (query: string) => void;
  clearSearch: () => void;
  placeholder: string;
};

const CONTEXT_PATHS: Record<SearchContext, string> = {
  all: "/browse",
  series: "/browse",
  releases: "/books",
  creators: "/creators",
  bundles: "/bundles",
};

const PATH_TO_CONTEXT: Record<string, SearchContext> = {
  browse: "series",
  books: "releases",
  creators: "creators",
  bundles: "bundles",
};

export const BROWSE_SEGMENTS = new Set(["browse", "books", "creators", "bundles"]);

const PLACEHOLDERS: Record<SearchContext, string> = {
  all: "Explore over 100,000 comics",
  series: "Search series",
  releases: "Search releases",
  creators: "Search creators",
  bundles: "Search bundles",
};

function getPlaceholder(ctx: ActiveSearchContext, channelMeta?: ChannelMeta): string {
  if (isChannelContext(ctx) && channelMeta) {
    return channelMeta.placeholder ?? `Explore ${channelMeta.displayName} Library`;
  }
  if (!isChannelContext(ctx)) {
    return PLACEHOLDERS[ctx];
  }
  return PLACEHOLDERS.all;
}

function resolveChannel(
  channel: GCChannel,
): { context: ChannelSearchContext; meta: ChannelMeta } | undefined {
  // The primary (GC) channel is the default — it shouldn't set a channel context
  if (channel.is_primary) return undefined;
  const config = channel.config?.[0];
  if (!config?.search_context) return undefined;

  return {
    context: {
      browseKey: config.search_context.browse_key,
      filters: config.search_context.filters,
    },
    meta: {
      icon: config.icon,
      displayName: channel.display_name,
      placeholder: config.search_context.placeholder,
    },
  };
}

function getSegment(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

const SearchV2Context = createContext<SearchContextState | undefined>(undefined);

export const SearchV2Provider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Only real state: user's context choice when NOT on a browse page
  const [manualContext, setManualContext] = useState<SearchContext>("all");
  // When user explicitly picks a non-channel context via dropdown, override channel scope
  const [channelOverridden, setChannelOverridden] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { channel: channelParam, slug } = useParams<{ channel?: string; slug?: string }>();
  const accountData = useAccountData();

  // Build slugsMap the same way RootScreen / useCurrentCMSStream do
  const slugsMap = useMemo(() => {
    if (!accountData) return undefined;
    const map: Record<string, GCChannel> = {};
    (accountData.home_tab_channels ?? []).forEach((ch) => {
      ch.streams.forEach((s) => {
        map[s.slug] = ch;
      });
    });
    return map;
  }, [accountData]);

  // Reverse lookup: browse_key → resolved channel
  const browseKeyMap = useMemo(() => {
    if (!accountData) return undefined;
    const map: Record<string, { context: ChannelSearchContext; meta: ChannelMeta }> = {};
    for (const ch of accountData.home_tab_channels ?? []) {
      const resolved = resolveChannel(ch);
      if (resolved) {
        map[resolved.context.browseKey] = resolved;
      }
    }
    return map;
  }, [accountData]);

  // Resolve the current page's channel (if any)
  const pageChannel = useMemo(() => {
    if (!slugsMap || !channelParam) return undefined;
    const matched = slugsMap[`${channelParam}/${slug}`] ?? slugsMap[channelParam];
    return matched ? resolveChannel(matched) : undefined;
  }, [slugsMap, channelParam, slug]);

  // Reset channel override when navigating to a different channel page
  useEffect(() => {
    setChannelOverridden(false);
  }, [pageChannel?.context.browseKey]);

  // Derived: channel scope from route or URL param (no state)
  const urlBrowseKey = useMemo(
    () => new URLSearchParams(location.search).get("browse_key"),
    [location.search],
  );

  const channelScope = useMemo(() => {
    if (pageChannel) return pageChannel;
    if (urlBrowseKey && browseKeyMap?.[urlBrowseKey]) return browseKeyMap[urlBrowseKey];
    return null;
  }, [pageChannel, urlBrowseKey, browseKeyMap]);

  // Derived: what the dropdown displays (no state)
  const displayContext = useMemo((): ActiveSearchContext => {
    // Channel scope takes priority unless user explicitly overrode it
    if (channelScope && !channelOverridden) return channelScope.context;
    // On browse pages: derive from path
    const segment = getSegment(location.pathname);
    const pathCtx = PATH_TO_CONTEXT[segment];
    if (pathCtx) return pathCtx;
    // Elsewhere: user's manual selection
    return manualContext;
  }, [channelScope, channelOverridden, location.pathname, manualContext]);

  const channelMeta = useMemo(
    () => channelScope?.meta ?? undefined,
    [channelScope],
  );

  // Dropdown selection: clear channel scope when user picks a standard context
  const setContext = useCallback(
    (ctx: SearchContext) => {
      const params = new URLSearchParams(location.search);
      // Leaving channel scope — remove browse_key from URL
      params.delete("browse_key");
      setChannelOverridden(true);

      const segment = getSegment(location.pathname);
      const onBrowsePage = BROWSE_SEGMENTS.has(segment);

      if (onBrowsePage) {
        const targetPath = CONTEXT_PATHS[ctx];
        const searchSuffix = params.toString() ? `?${params}` : "";
        navigate(`${targetPath}${searchSuffix}`, { replace: true });
      } else {
        // Off browse: just scope instant search, don't navigate
        setManualContext(ctx);
      }
    },
    [location, navigate],
  );

  // Restore channel context from dropdown (only available on channel pages)
  const restoreChannelContext = useCallback(() => {
    if (!pageChannel) return;
    setChannelOverridden(false);
    // If we're still on the channel page, just clearing the override is enough
    // Only navigate if we've moved to a different browse page
    if (!channelParam) {
      const params = new URLSearchParams(location.search);
      params.set("browse_key", pageChannel.context.browseKey);
      navigate(`/browse?${params.toString()}`, { replace: true });
    }
  }, [pageChannel, channelParam, location.search, navigate]);

  // Commit a search query (enter key / search button)
  const activeChannelScope = channelScope && !channelOverridden ? channelScope : null;
  const commitSearch = useCallback(
    (query: string) => {
      const params = new URLSearchParams();
      params.set("q", query);
      if (activeChannelScope) {
        params.set("browse_key", activeChannelScope.context.browseKey);
      }
      const path = activeChannelScope
        ? "/browse"
        : (isChannelContext(displayContext) ? "/browse" : CONTEXT_PATHS[displayContext]);
      navigate(`${path}?${params.toString()}`);
    },
    [activeChannelScope, displayContext, navigate],
  );

  const clearSearch = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("q");
    const search = params.toString();
    navigate(search ? `${location.pathname}?${search}` : location.pathname, {
      replace: true,
    });
  }, [navigate, location.pathname, location.search]);

  const value = useMemo<SearchContextState>(
    () => ({
      context: displayContext,
      channelMeta: channelMeta ?? pageChannel?.meta,
      isOnChannelPage: !!pageChannel,
      setContext,
      restoreChannelContext,
      commitSearch,
      clearSearch,
      placeholder: getPlaceholder(displayContext, channelMeta),
    }),
    [displayContext, channelMeta, pageChannel, setContext, restoreChannelContext, commitSearch, clearSearch],
  );

  return (
    <SearchV2Context.Provider value={value}>
      {children}
    </SearchV2Context.Provider>
  );
};

const DEFAULT_SEARCH_CONTEXT: SearchContextState = {
  context: "all",
  channelMeta: undefined,
  isOnChannelPage: false,
  setContext: () => {},
  restoreChannelContext: () => {},
  commitSearch: () => {},
  clearSearch: () => {},
  placeholder: PLACEHOLDERS.all,
};

export const useSearchV2 = (): SearchContextState => {
  const context = useContext(SearchV2Context);
  return context ?? DEFAULT_SEARCH_CONTEXT;
};
